/**
 * Generative music core (race audio 2, P4): pure, DOM-free, shared by the ambience AudioWorklet and the node tests.
 *
 * `planOf(seed)` fixes one key, mode, tempo and 4-chord progression per race (the seed is a hash of the race id);
 * `barEvents(plan, bar)` lists every note a bar could play. The list does not depend on intensity: each note
 * carries a density threshold `thr` and only sounds once the smoothed intensity has reached it, so the same seed
 * always gives the same note events. Layers: slow pad chords (two detuned band-limited saws per note), a bass line,
 * a soft low pulse, plucked arpeggio notes, hi-hats. Intensity (0..1, smoothed over ~2 s) raises the tempo by up to
 * 8 %, opens the low-pass on the pad and arpeggio, and lets more notes in. A chequered-flag sting is a stacked
 * tonic chord. The progression is a seeded walk over the mode's degrees; it is not any known tune.
 * Everything is seeded (mulberry32), no Math.random.
 */
import { mulberry32 } from './engine-dsp.ts';

export const BEATS_PER_BAR = 4;
export const PROGRESSION_BARS = 8;           // 4 chords, 2 bars each
const TEMPO_UP = 0.08;                       // tempo at intensity 1 is +8 %
const INTENSITY_TC_S = 2;

const MODES: readonly (readonly number[])[] = [
  [0, 2, 3, 5, 7, 9, 10],   // dorian
  [0, 2, 3, 5, 7, 8, 10],   // aeolian
  [0, 2, 4, 5, 7, 9, 10],   // mixolydian
  [0, 2, 4, 6, 7, 9, 11],   // lydian
];

/** FNV-1a of the race id: the music seed */
export function seedOf(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 0x01000193) >>> 0;
  return h >>> 0;
}

export interface MusicPlan {
  seed: number;
  /** pitch class of the tonic, 0..11 */
  tonic: number;
  mode: readonly number[];
  bpm: number;
  /** pad chords as midi notes, one per 2 bars */
  chords: number[][];
  /** bass root midi (36..47) per chord */
  roots: number[];
  /** the tonic triad for the sting */
  sting: number[];
}

const degreeMidi = (tonic: number, mode: readonly number[], d: number, base: number): number =>
  base + tonic + (mode[d % 7] ?? 0) + 12 * Math.floor(d / 7);

export function planOf(seed: number): MusicPlan {
  const rnd = mulberry32(seed ^ 0x9e3779b9);
  const tonic = Math.floor(rnd() * 12);
  const mode = MODES[Math.floor(rnd() * MODES.length)] ?? [];
  const bpm = 82 + Math.floor(rnd() * 20);
  const degrees = [0];
  while (degrees.length < 4) {
    const d = Math.floor(rnd() * 7);
    if (d !== degrees[degrees.length - 1]) degrees.push(d);
  }
  const chords = degrees.map((d) => {
    const seventh = rnd() < 0.5;
    const stack = seventh ? [0, 2, 4, 6] : [0, 2, 4];
    return stack.map((k) => degreeMidi(tonic, mode, d + k, 48));
  });
  const roots = degrees.map((d) => 36 + ((tonic + (mode[d] ?? 0)) % 12));
  return { seed, tonic, mode, bpm, chords, roots, sting: [0, 4, 7, 9].map((d) => degreeMidi(tonic, mode, d, 48)) };
}

export type Layer = 'pad' | 'bass' | 'pulse' | 'arp' | 'hat';
export interface NoteEvent {
  bar: number;
  /** beats from the bar start */
  beat: number;
  layer: Layer;
  midi: number;
  /** beats */
  len: number;
  /** the note sounds once the smoothed intensity is at least this */
  thr: number;
  /** -1..1 */
  pan: number;
}

/** every note a bar could play, sorted by beat (stable), from (plan, bar) alone */
export function barEvents(plan: MusicPlan, bar: number): NoteEvent[] {
  const rnd = mulberry32(plan.seed + 7919 * (bar + 1));
  const ci = Math.floor(bar / 2) % 4, chord = plan.chords[ci] ?? [], root = plan.roots[ci] ?? 36;
  const out: NoteEvent[] = [];
  const add = (beat: number, layer: Layer, midi: number, len: number, thr: number, pan = 0): void => {
    out.push({ bar, beat, layer, midi, len, thr, pan });
  };
  if (bar % 2 === 0) for (const m of chord) add(0, 'pad', m, 8, 0);
  add(0, 'bass', root, 3.5, 0);
  add(2.5, 'bass', root, 1, 0.35);
  add(3.5, 'bass', root + (rnd() < 0.5 ? 7 : 12), 0.5, 0.6);
  add(0, 'pulse', 0, 1, 0);
  for (const b of [1, 2, 3]) add(b, 'pulse', 0, 1, 0.3);
  for (let i = 0; i < 8; i++) {
    const thr = i === 0 ? 0.25 : 0.3 + 0.6 * rnd();
    const m = (chord[Math.floor(rnd() * chord.length)] ?? 60) + (rnd() < 0.5 ? 12 : 24);
    add(i / 2, 'arp', m, 0.5, thr, (rnd() - 0.5) * 1.2);
  }
  for (let i = 0; i < 4; i++) add(i + 0.5, 'hat', 0, 0.25, 0.35, (i % 2 ? 1 : -1) * 0.25);
  for (let i = 0; i < 8; i++) add(i / 2 + 0.25, 'hat', 0, 0.25, 0.7, (i % 2 ? 1 : -1) * 0.25);
  return out.sort((a, b) => a.beat - b.beat);
}

const hz = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);

/** band-limited saw, phase 0..1, phase step dt */
function saw(ph: number, dt: number): number {
  let s = 2 * ph - 1;
  if (ph < dt) { const t = ph / dt; s -= t + t - t * t - 1; }
  else if (ph > 1 - dt) { const t = (ph - 1) / dt; s -= t * t + t + t + 1; }
  return s;
}

export interface MusicParams { presT: number; intensity: number; sting: boolean; active: boolean }

const MAX_NOTES = 28;
interface Note {
  layer: Layer | 'sting';
  on: boolean;
  f: number; ph: number; ph2: number; age: number; len: number; amp: number; pan: number; env: number;
}
const freshNote = (): Note => ({ layer: 'pad', on: false, f: 0, ph: 0, ph2: 0, age: 0, len: 0, amp: 0, pan: 0, env: 0 });

export class MusicSynth {
  private plan: MusicPlan;
  private readonly notes: Note[] = Array.from({ length: MAX_NOTES }, freshNote);
  private bar = 0;
  private beat = 0;
  private events: NoteEvent[] = [];
  private ei = 0;
  private inten = 0;
  private tInten = 0;
  private lastPres = Number.NaN;
  private wasActive = false;
  private rnd: () => number;
  private readonly lpPadL: [number, number] = [0, 0]; private readonly lpPadR: [number, number] = [0, 0];
  private hatLp = 0;
  private bassLp = 0;

  constructor(private readonly sr: number, seed = 0) {
    this.plan = planOf(seed);
    this.rnd = mulberry32(seed ^ 0x51ed270b);
    this.loadBar(0, 0);
  }

  get currentPlan(): MusicPlan { return this.plan; }

  setSeed(seed: number): void {
    this.plan = planOf(seed);
    this.rnd = mulberry32(seed ^ 0x51ed270b);
    for (const n of this.notes) n.on = false;
    this.inten = this.tInten;
    this.lastPres = Number.NaN;
    this.loadBar(0, 0);
  }

  private loadBar(bar: number, beat: number): void {
    this.bar = bar; this.beat = beat;
    this.events = barEvents(this.plan, bar);
    this.ei = this.events.findIndex((e) => e.beat >= beat);
    if (this.ei < 0) this.ei = this.events.length;
  }

  /** place the playhead at presentation time t (base tempo); sounding notes are released, not cut */
  private seek(presT: number): void {
    const beats = Math.max(0, presT) * this.plan.bpm / 60;
    for (const n of this.notes) if (n.on) n.len = Math.min(n.len, n.age);
    const bar = Math.floor(beats / BEATS_PER_BAR), beat = beats % BEATS_PER_BAR;
    this.loadBar(bar, beat);
    // the pad chord that began on the even bar before: bring it in for what is left of its 8 beats
    const padBar = bar - (bar % 2), left = 8 - ((bar - padBar) * BEATS_PER_BAR + beat);
    for (const e of barEvents(this.plan, padBar)) if (e.layer === 'pad' && !(padBar === bar && e.beat >= beat)) this.trigger({ ...e, len: left });
  }

  /** Apply the next block's params: a presentation jump of more than 1 s, or a pause -> play edge, resyncs the playhead. */
  setParams(p: MusicParams): void {
    this.tInten = Math.min(Math.max(p.intensity, 0), 1);
    if (!this.wasActive && p.active || Math.abs(p.presT - this.lastPres) > 1) this.seek(p.presT);
    this.wasActive = p.active; this.lastPres = p.presT;
    if (p.sting) this.sting();
  }

  private sting(): void {
    this.plan.sting.forEach((m, i) => {
      const n = this.alloc();
      Object.assign(n, { layer: 'sting', on: true, f: hz(m), ph: 0, ph2: 0.3 * i, age: 0, len: 0.05 * this.sr, amp: 0.45, pan: (i - 1.5) * 0.3, env: 0 });
    });
  }

  private alloc(): Note {
    let best = this.notes[0] as Note;
    for (const n of this.notes) {
      if (!n.on) return n;
      if (n.age > best.age) best = n;
    }
    return best;
  }

  private trigger(e: NoteEvent): void {
    const sr = this.sr, beatS = 60 / this.plan.bpm;
    const n = this.alloc();
    const base = { on: true, ph: this.rnd(), ph2: this.rnd(), age: 0, env: 0, pan: e.pan, f: e.layer === 'pulse' || e.layer === 'hat' ? 0 : hz(e.midi) };
    if (e.layer === 'pad') Object.assign(n, base, { layer: 'pad', len: e.len * beatS * sr, amp: 0.05, pan: (this.rnd() - 0.5) * 0.4 });
    else if (e.layer === 'bass') Object.assign(n, base, { layer: 'bass', len: e.len * beatS * sr, amp: 0.2 });
    else if (e.layer === 'arp') Object.assign(n, base, { layer: 'arp', len: 0.4 * sr, amp: 0.09 });
    else if (e.layer === 'pulse') Object.assign(n, base, { layer: 'pulse', len: 0.2 * sr, amp: e.beat === 0 ? 0.28 : 0.16 });
    else Object.assign(n, base, { layer: 'hat', len: (e.thr > 0.5 ? 0.03 : 0.06) * sr, amp: e.thr > 0.5 ? 0.05 : 0.08 });
  }

  /** left/right out, `gain` is the music bus level */
  process(outL: Float32Array, outR: Float32Array, gain: number, n = outL.length): void {
    const sr = this.sr, TWO_PI = 2 * Math.PI;
    const aInt = 1 - Math.exp(-1 / (INTENSITY_TC_S * sr));
    for (let i = 0; i < n; i++) {
      this.inten += aInt * (this.tInten - this.inten);
      const it = this.inten;
      this.beat += (this.plan.bpm / 60) * (1 + TEMPO_UP * it) / sr;
      while (this.beat >= BEATS_PER_BAR) this.loadBar(this.bar + 1, this.beat - BEATS_PER_BAR);
      while (this.ei < this.events.length && (this.events[this.ei] as NoteEvent).beat <= this.beat) {
        const e = this.events[this.ei++] as NoteEvent;
        if (it >= e.thr) this.trigger(e);
      }
      let pl = 0, pr = 0, dl = 0, dr = 0, bass = 0;
      for (const v of this.notes) {
        if (!v.on) continue;
        const t = v.age++;
        switch (v.layer) {
          case 'pad': {
            // slow swell in over 1.4 s, 1.8 s release after len
            const a = Math.min(1, t / (1.4 * sr)), r = t < v.len ? 1 : Math.max(0, 1 - (t - v.len) / (1.8 * sr));
            if (r <= 0) { v.on = false; break; }
            const dt1 = v.f * 1.003 / sr, dt2 = v.f * 0.997 / sr;
            v.ph = (v.ph + dt1) % 1; v.ph2 = (v.ph2 + dt2) % 1;
            const g = v.amp * a * r;
            const x1 = saw(v.ph, dt1) * g, x2 = saw(v.ph2, dt2) * g;
            const wl = 0.5 - 0.5 * v.pan, wr = 0.5 + 0.5 * v.pan;
            pl += x1 * (0.5 + wl) + x2 * 0.5; pr += x2 * (0.5 + wr) + x1 * 0.5;
            break;
          }
          case 'bass': {
            const a = Math.min(1, t / (0.01 * sr)), r = t < v.len ? 1 : Math.max(0, 1 - (t - v.len) / (0.12 * sr));
            if (r <= 0) { v.on = false; break; }
            v.ph = (v.ph + v.f / sr) % 1;
            bass += (Math.sin(TWO_PI * v.ph) + 0.35 * Math.sin(TWO_PI * 2 * v.ph)) * v.amp * a * r;
            break;
          }
          case 'arp': {
            if (t >= v.len) { v.on = false; break; }
            const e = Math.exp(-6 * t / v.len) * Math.min(1, t / (0.004 * sr));
            v.ph = (v.ph + v.f / sr) % 1;
            const x = (Math.sin(TWO_PI * v.ph) + 0.4 * Math.sin(TWO_PI * 3 * v.ph) * e) * v.amp * e;
            pl += x * (0.5 - 0.5 * v.pan); pr += x * (0.5 + 0.5 * v.pan);
            break;
          }
          case 'pulse': {
            if (t >= v.len) { v.on = false; break; }
            const e = Math.exp(-7 * t / v.len), f = 46 + 50 * Math.exp(-30 * t / sr);
            v.ph = (v.ph + f / sr) % 1;
            bass += Math.sin(TWO_PI * v.ph) * v.amp * e;
            break;
          }
          case 'hat': {
            if (t >= v.len) { v.on = false; break; }
            const white = this.rnd() * 2 - 1;
            this.hatLp += 0.5 * (white - this.hatLp);
            const x = (white - this.hatLp) * v.amp * (0.5 + 0.5 * it) * (1 - t / v.len) ** 2;
            dl += x * (0.5 - 0.5 * v.pan); dr += x * (0.5 + 0.5 * v.pan);
            break;
          }
          default: {                                  // sting: swell, long decay
            const a = Math.min(1, t / (0.03 * sr)), e = Math.exp(-t / (1.1 * sr));
            if (e < 0.003) { v.on = false; break; }
            const dt = v.f / sr;
            v.ph = (v.ph + dt) % 1; v.ph2 = (v.ph2 + dt * 1.004) % 1;
            const x = (saw(v.ph, dt) + saw(v.ph2, dt * 1.004)) * 0.5 * v.amp * a * e;
            dl += x * (0.5 - 0.5 * v.pan); dr += x * (0.5 + 0.5 * v.pan);      // past the pad low-pass: the sting stays bright
          }
        }
      }
      // pad and arpeggio: two cascaded one-pole low-passes, cutoff opens with intensity
      const fc = 450 + 4200 * it * it, k = 1 - Math.exp(-TWO_PI * fc / sr);
      this.lpPadL[0] = this.lpPadL[0] + k * (pl - this.lpPadL[0]); this.lpPadL[1] = this.lpPadL[1] + k * (this.lpPadL[0] - this.lpPadL[1]);
      this.lpPadR[0] = this.lpPadR[0] + k * (pr - this.lpPadR[0]); this.lpPadR[1] = this.lpPadR[1] + k * (this.lpPadR[0] - this.lpPadR[1]);
      this.bassLp += 0.2 * (bass - this.bassLp);
      outL[i] = Math.tanh(this.lpPadL[1] + this.bassLp + dl) * gain;
      outR[i] = Math.tanh(this.lpPadR[1] + this.bassLp + dr) * gain;
    }
  }
}
